/**
 * The two navigations, as data.
 *
 * `.docs/00-index.md` §5: "Nav, titles, badges → `apps/web/src/shell/nav.ts`. Pages
 * set `<PageTitle>`; nothing else knows the nav." This is a port of `NAV` in
 * `mockups/assets/shell.js`, with three differences and no fourth:
 *
 *   1. **`href` is a router path, not a file.** `dashboard.html` → `/app`,
 *      `analytics.html` → `/studio`. The mapping is one column and is the whole of
 *      the change; the groups, their order and their labels are the mockup's.
 *   2. **Badges are a key, not a number.** `shell.js` hard-codes `badge: '3'`
 *      because it renders a screenshot. Here an item names which badge it wants
 *      and `useNavBadges()` answers — with `undefined` until the session that owns
 *      the query lands, and an undefined badge does not render.
 *   3. **A group may be filled at runtime.** The client's widget items are one per
 *      widget that client has (session 13), so the Widgets group carries
 *      `dynamic: 'widgets'` and no items. Until 13 exists the hook answers nothing
 *      and the group renders nothing at all — not an empty heading.
 *   4. **An item says who it is for.** `shell.js` had one reader and no roles.
 *      Here `needs` is a **required** column, so the nav is still data and the
 *      filter still cannot be forgotten — see `NavNeed`.
 *
 * `page` is the key `shell.js` matched the active link on. Here the active link is
 * decided by the router (`Link` with `activeProps`), so `page` survives only as the
 * stable id a test and a badge key can name.
 */

import type { SettingsAbilities } from '../pages/client/settings/roles.js'

/** Which badge an item shows. `useNavBadges()` is the one place they are counted. */
export type BadgeKey =
  'open-decisions' | 'files-review' | 'mockups-review' | 'overdue-tasks' | 'review-queue'

/** Which runtime list fills a group. Session 13 answers the only one there is. */
export type DynamicGroup = 'widgets'

/**
 * What a nav item needs of the person reading it, as an **ability**.
 *
 * `.docs/19-client-portal-pages.md` §Edge cases: "`billing` role user: sees billing,
 * settings (payment), invoices; requests/projects/files show the `Forbidden` problem
 * inline as the one line, **nav items hidden**." Line 123 of the same file makes it a
 * repository rule rather than one page's: a control somebody would be refused for is
 * *absent*, not disabled — and a nav item is a control.
 *
 * Three decisions are in this one type, and each is the reason it is not something
 * simpler:
 *
 *   1. **It is a `keyof SettingsAbilities`, not a `ClientPermission`.** `abilitiesOf`
 *      is the single reader of `ROLE_PERMISSIONS` on this side of the boundary
 *      (`pages/client/settings/roles.ts`), so naming an ability means the nav can
 *      never claim a rule that file has not written down. Naming a permission would
 *      have put a second reading of the matrix in the shell.
 *   2. **It is required, not optional.** An optional `needs` defaults a new item to
 *      "everyone", which is precisely the mistake this fixes — the nav shipped with
 *      no filter at all and every role saw every page. Leaving it off is now a type
 *      error, which is the only kind of reminder that survives a year.
 *   3. **`'anyone'` is a word rather than an absence**, for the same reason: an
 *      author has to look at the item and decide, and a reviewer can see that they
 *      did.
 *
 * ## The studio half, session 35
 *
 * This paragraph used to read: "The studio nav is `'anyone'` throughout… the studio
 * portal's own rules are per-control rather than per-page." That stopped being true
 * the day the studio grew two tiers. Three of its four items — Analytics, Clients and
 * Money — are a manager's pages, and a restricted account offered them would follow a
 * link straight into `auth/guards.ts`'s redirect, which is the "control that can only
 * refuse" this type exists to prevent.
 *
 * `'manager'` is a fourth member of the union rather than a second column on
 * `NavItem`, and that keeps all three decisions above intact: an optional `tier`
 * beside `needs` would default a new item to open, which is decision 2's exact
 * mistake. It is not a `keyof SettingsAbilities` because there is nothing for it to
 * be — that type is a *client seat*'s vocabulary and `abilitiesOf` answers all-false
 * for a studio principal, so a studio item marked with one would be permanently
 * invisible. This is the `SwitchNeed` precedent: the one question the shell already
 * asks about a principal's *kind* rather than about a client role.
 */
export type NavNeed = 'anyone' | 'manager' | keyof SettingsAbilities

export type NavItem = {
  readonly page: string
  readonly href: string
  readonly text: string
  readonly badge?: BadgeKey
  /** Who this item is for. See `NavNeed` — there is no default on purpose. */
  readonly needs: NavNeed
}

export type NavGroup = {
  readonly label: string
  readonly items: readonly NavItem[]
  /**
   * When present, `items` is a floor and the group is completed at runtime. A group
   * that ends up with nothing in it is not rendered — an empty "Widgets" heading is
   * chrome that has not earned its place (DESIGN.md P7).
   */
  readonly dynamic?: DynamicGroup
}

/**
 * Who the link to the *other* portal is for.
 *
 * R2-33: "→ Studio portal" was in every client's sidebar — owner, admin, billing
 * and member alike — and following it bounced straight back to `/app?notice=
 * staff-only`. The notice is a good one, but a control offered permanently in
 * somebody's own chrome that can only ever refuse them is not a notice problem;
 * `.docs/19-client-portal-pages.md` line 123 is the repository rule and it is the
 * same one `NavNeed` already applies to every item above: a control somebody would
 * be refused for is **absent**, not disabled.
 *
 * It is not a `NavNeed`, and that is the point rather than an omission. Every
 * `NavNeed` is an ability of a *client seat*, and the person this link is for holds
 * none — `abilitiesOf` has nothing to answer with for a staff principal. The
 * question here is which kind of principal is reading, which is one question with
 * two answers and no matrix behind it.
 */
export type SwitchNeed = 'anyone' | 'staff'

export type NavConfig = {
  /** The line beside the wordmark. `shell.js` `cfg.label`. */
  readonly label: string
  /** The chip beside the wordmark. Studio only, exactly as in the mockups. */
  readonly tag: string | null
  readonly groups: readonly NavGroup[]
  readonly foot: readonly NavItem[]
  readonly switchTo: {
    readonly href: string
    readonly text: string
    /** See `SwitchNeed`. Required, for the reason `NavNeed` is. */
    readonly needs: SwitchNeed
  }
}

export type Portal = 'client' | 'studio'

/**
 * `mockups/assets/shell.js` `NAV.client`, with `.html` files replaced by routes.
 *
 * The v3/v4 nav decisions recorded in that file's header hold and are not
 * relitigated here: "Money" is "Services & billing", the seven-page Services group
 * is gone, Approvals is folded into Requests (which is why the decisions badge is
 * on Requests), and every group is flat so the ≤1023px scroller needs no folding.
 *
 * **Three items are `raisesRequests` and the rest are `anyone`**, and the line
 * between them is `.docs/19` §Edge cases read literally: it names requests, projects
 * and files as the pages a `billing` seat is refused, and it names no others. The
 * dashboard, the calls page, the monthly reports and the widget pages are left open
 * deliberately rather than by omission — `/app` is where signing in lands, so
 * refusing it would need a second landing rule in `auth/guards.ts`, and the session
 * file does not ask for one. Widening this is a decision for the lead, and it is one
 * line per item when it is taken.
 */
export const CLIENT_NAV: NavConfig = {
  label: 'Client portal',
  tag: null,
  groups: [
    {
      label: 'Overview',
      items: [
        { page: 'dashboard', href: '/app', text: 'Dashboard', needs: 'anyone' },
        { page: 'billing', href: '/app/billing', text: 'Services & billing', needs: 'anyone' },
        {
          page: 'requests',
          href: '/app/requests',
          text: 'Requests',
          badge: 'open-decisions',
          needs: 'raisesRequests',
        },
      ],
    },
    {
      label: 'Work',
      items: [
        /**
         * Session 43, and the one item in this nav that is not in the mockups.
         *
         * It is here because the dashboard's "What we are working on" block is
         * bounded at five and `.docs/24`'s scale rule is that a bounded list refers
         * onwards — and for a task there was nowhere to refer to. Projects is
         * fixed-scope work and says so in its own first line; Files is deliverables.
         * One production client has twenty-four live tasks.
         *
         * **`needs: 'anyone'`**, and it is a decision rather than the absence of one.
         * `.docs/19` §Edge cases names requests, projects and files as the three a
         * `billing` seat is refused, and it names no others; this page carries no
         * money, offers no control and asks for no decision, so there is nothing on
         * it a seat could be refused for. It is the same reasoning that leaves the
         * dashboard — which already carries five of these rows — open to every seat.
         */
        { page: 'work', href: '/app/work', text: 'What we’re working on', needs: 'anyone' },
        { page: 'projects', href: '/app/projects', text: 'Projects', needs: 'raisesRequests' },
        {
          page: 'files',
          href: '/app/files',
          text: 'Files',
          badge: 'files-review',
          needs: 'raisesRequests',
        },
        /*
         * Session 48: the designs the studio shares for feedback. Open to every seat: a
         * mockup is public to anyone with its link, so there is nothing to refuse a seat by
         * showing it. Answering and commenting are `requests:write`, and the page offers
         * those controls only to the seats that have it (`ClientMockupPage.tsx`).
         */
        {
          page: 'mockups',
          href: '/app/mockups',
          text: 'Mockups',
          badge: 'mockups-review',
          needs: 'anyone',
        },
        { page: 'calls', href: '/app/calls', text: 'Calls', needs: 'anyone' },
      ],
    },
    {
      label: 'Reports',
      items: [{ page: 'reports', href: '/app/reports', text: 'Monthly reports', needs: 'anyone' }],
    },
    {
      /**
       * `shell.js` lists Casaturca's widgets by name — three of them since session
       * 25. They are that client's rows in `widget`, not the product's navigation,
       * so the group is filled from `GET /api/clients/me/widgets` (session 13) and
       * is empty here.
       */
      label: 'Widgets',
      items: [],
      dynamic: 'widgets',
    },
  ],
  /*
   * Settings is `anyone` because `.docs/19` §Edge cases puts it in the list a
   * `billing` seat *does* see — "billing, settings (payment), invoices". The page
   * itself already hides the regions that role may not use: `SettingsPage` reads
   * `abilitiesOf`, and `seesPeople` is false for exactly this role.
   */
  foot: [{ page: 'settings', href: '/app/settings', text: 'Settings', needs: 'anyone' }],
  /**
   * `'staff'`: the client portal is the one shell a staff member legitimately
   * reads — the studio's own people open a client's screen to see what the client
   * sees — and this is their way back out. It is nobody else's: a client seat has
   * no studio account, so for every one of the four client roles this link could
   * only ever end at `/app?notice=staff-only`.
   *
   * An **impersonating** staff member is a `client_user` (`auth/principal.tsx`:
   * "impersonation is a client session"), so they do not see it either — which is
   * right rather than incidental. Following it would carry the client's session
   * into `/studio` and be refused there by the same guard; their way back is
   * *Stop impersonating* in the banner, which is on the screen above this one.
   */
  switchTo: { href: '/studio', text: 'Studio portal', needs: 'staff' },
}

/**
 * `NAV.studio`. One group, as in the mockups.
 *
 * `client-detail.html` is not a nav item here: it is `/studio/clients/$slug`
 * (session 20), reached from the Clients list, and a nav link to "a client" with no
 * client in it was a mockup convenience.
 *
 * **Tasks is the only `'anyone'`** (session 35), and it is the whole of what a
 * restricted studio account sees. The other three are `'manager'`, which is the same
 * set `auth/guards.ts` `STUDIO_ROUTE_ROLE` refuses — two statements of one rule, and
 * `nav.unit.test.ts` is what holds them together: a nav item nobody can reach and a
 * reachable page with no nav item are both failing tests.
 */
export const STUDIO_NAV: NavConfig = {
  label: 'Studio portal',
  tag: 'STUDIO',
  groups: [
    {
      label: 'Studio',
      items: [
        { page: 'analytics', href: '/studio', text: 'Analytics', needs: 'manager' },
        { page: 'clients', href: '/studio/clients', text: 'Clients', needs: 'manager' },
        {
          page: 'tasks',
          href: '/studio/tasks',
          text: 'Tasks',
          badge: 'overdue-tasks',
          needs: 'anyone',
        },
        {
          page: 'money',
          href: '/studio/money',
          text: 'Money',
          badge: 'review-queue',
          needs: 'manager',
        },
      ],
    },
  ],
  foot: [],
  /**
   * `'anyone'`, and it means everybody who can see this nav at all — which
   * `auth/guards.ts` has already narrowed to staff. Switching *to* the client
   * portal is the legitimate direction and it keeps working; there is no second
   * rule here, only the absence of one.
   */
  switchTo: { href: '/app', text: 'Client portal', needs: 'anyone' },
}

export function navFor(portal: Portal): NavConfig {
  return portal === 'studio' ? STUDIO_NAV : CLIENT_NAV
}

/**
 * `document.title`, exactly as `shell.js` builds it:
 * `<page> — Far East Studios <portal label>`.
 */
export function documentTitle(portal: Portal, pageTitle: string): string {
  return `${pageTitle} — Far East Studios ${navFor(portal).label}`
}

/** The studio's own org line until session 17's `analytics_summary` fills it in. */
export const STUDIO_ORG_NAME = 'Far East Studios'

/** Sign out is a button, not a link: `shell.js` pointed it at `login.html`. */
export const SIGN_OUT_LABEL = 'Sign out'
